import { Components, Theme as MuiTheme } from '@mui/material';
import { alpha } from '@mui/material';
import { designTokens } from '../themes/design-tokens';

// Common typography styles for input/textarea elements
const inputTypographyStyles = {
  fontFamily: 'inherit',
  fontSize: designTokens.typography.fontSizes.body2,
  fontStyle: 'normal',
  fontWeight: designTokens.typography.fontWeights.regular,
  lineHeight: 1.2,
  letterSpacing: designTokens.typography.letterSpacing.body2,
};

// Common input styling with proper vertical centering
const getInputStyles = () => ({
  ...inputTypographyStyles,
  padding: '0 16px',
  boxSizing: 'border-box',
  '&::placeholder': {
    color: 'inherit',
    opacity: 0.6,
  },
});

// Common adornment positioning
const adornmentStyles = {
  '& .MuiInputAdornment-positionStart': {
    marginLeft: '16px',
    marginRight: '6px',
  },
  '& .MuiInputAdornment-positionEnd': {
    marginRight: '16px',
    marginLeft: '6px',
  },
};

// Common state styles generator
const createStateStyles = (theme: MuiTheme) => ({
  hover: {
    backgroundColor: alpha(theme.palette.text.second, 0.05),
    borderColor: theme.palette.text.main,
  },
  focused: {
    borderColor: theme.palette.text.second,
    borderWidth: '1px',
  },
  error: {
    borderColor: theme.palette.error.main,
  },
  disabled: {
    borderColor: alpha(theme.palette.text.second, 0.3),
    backgroundColor: alpha(theme.palette.text.second, 0.1),
  },
});

// Common border styles
const baseBorderStyles = {
  borderRadius: designTokens.textField.borderRadius,
  borderWidth: '1px',
};

export const textFieldTheme: Components<MuiTheme> = {
  MuiTextField: {
    styleOverrides: {
      root: ({ theme }) => {
        const states = createStateStyles(theme);

        return {
          fontFamily: theme.typography.fontFamily,
          transition: theme.transitions.create(['all'], {
            duration: theme.transitions.duration.short,
          }),

          // Custom rounded variant
          '&.TextField-rounded': {
            '& .MuiOutlinedInput-root': {
              height: theme.spacing(12), // 48px
              borderRadius: '90px',

              '& input': {
                height: theme.spacing(12), // 48px
                padding: '0 24px',
              },
              '& input:-webkit-autofill': {
                WebkitBoxShadow: `0 0 0 100px ${theme.palette.background.paper} inset !important`,
              },
              '& fieldset': {
                borderWidth: '2px',
                borderColor: theme.palette.text.second,
                borderRadius: '90px',
                transition: theme.transitions.create(
                  ['border-color', 'box-shadow'],
                  {
                    duration: theme.transitions.duration.shorter,
                    easing: theme.transitions.easing.easeInOut,
                  },
                ),
              },
              '&:hover:not(.Mui-disabled) fieldset': {
                borderColor: theme.palette.primary.light,
                boxShadow: `0 0 10px 1px ${theme.palette.primary.light}`,
              },
              '&.Mui-focused fieldset': {
                borderWidth: '2px',
                borderColor: theme.palette.primary.main,
                boxShadow: `0 0 10px 0 ${theme.palette.primary.main}`,
              },
              '&.Mui-error fieldset': {
                borderWidth: '2px',
                borderColor: theme.palette.error.main,
                boxShadow: `0 0 10px 0 ${theme.palette.error.main}`,
              },
              '&.Mui-disabled fieldset': {
                borderColor: theme.palette.text.fourth,
              },
            },
            '& .MuiInputAdornment-positionStart': {
              marginLeft: '24px',
              marginRight: '6px',
            },
            '& .MuiInputAdornment-positionEnd': {
              marginRight: '24px',
              marginLeft: '6px',
            },
          },

          // Label styling for all variants
          '& .MuiInputLabel-root': {
            color: theme.palette.text.second,
            '&.Mui-focused': {
              color: theme.palette.text.second,
            },
            '&.Mui-error': {
              color: theme.palette.error.main,
            },
          },

          // Helper text styling with 6px spacing
          '& .MuiFormHelperText-root': {
            marginTop: '6px',
            marginLeft: 0,
            marginRight: 0,
            fontSize: designTokens.typography.fontSizes.caption,
            lineHeight: designTokens.typography.lineHeights.caption,
            '&.Mui-error': {
              color: theme.palette.error.main,
            },
          },

          // Outlined Input
          '& .MuiOutlinedInput-root': {
            ...baseBorderStyles,
            '& textarea': {
              ...inputTypographyStyles,
              padding: '16px',
              boxSizing: 'border-box',
            },
            ...adornmentStyles,
            '& fieldset': {
              ...baseBorderStyles,
              borderColor: theme.palette.text.fourth,
            },
            '&:hover:not(.Mui-disabled)': {
              backgroundColor: states.hover.backgroundColor,
              '& fieldset': {
                borderColor: states.hover.borderColor,
              },
            },
            '&.Mui-focused': {
              '& fieldset': states.focused,
            },
            '&.Mui-error fieldset': {
              borderColor: states.error.borderColor,
            },
            '&.Mui-disabled': {
              '& fieldset': {
                borderColor: states.disabled.borderColor,
                backgroundColor: states.disabled.backgroundColor,
              },
            },
          },

          // Filled Input
          '& .MuiFilledInput-root': {
            ...baseBorderStyles,
            border: `1px solid ${theme.palette.text.fourth}`,
            '& input': getInputStyles(),
            '& textarea': {
              ...inputTypographyStyles,
              padding: '16px',
              boxSizing: 'border-box',
            },
            ...adornmentStyles,
            '&:hover': states.hover,
            '&.Mui-focused': {
              borderColor: states.focused.borderColor,
            },
            '&.Mui-error': {
              borderColor: states.error.borderColor,
            },
            '&.Mui-disabled': states.disabled,
          },

          // Standard Input
          '& .MuiInput-root': {
            borderRadius: baseBorderStyles.borderRadius,
            '& input': getInputStyles(),
            '& textarea': {
              ...inputTypographyStyles,
              padding: '16px',
              boxSizing: 'border-box',
            },
            ...adornmentStyles,
            '&:before': {
              borderBottomColor: theme.palette.text.fourth,
            },
            '&:hover:not(.Mui-disabled)': {
              backgroundColor: states.hover.backgroundColor,
              '&:before': {
                borderBottomColor: states.hover.borderColor,
              },
            },
            '&:after': {
              borderBottomColor: states.focused.borderColor,
            },
            '&.Mui-error:before': {
              borderBottomColor: states.error.borderColor,
            },
            '&.Mui-disabled': {
              '&:before': {
                borderBottomColor: states.disabled.borderColor,
              },
              backgroundColor: states.disabled.backgroundColor,
            },
          },
        };
      },
    },
  },
};
